import { useClerk, useUser } from "@clerk/clerk-react";
import {
    ArrowPathIcon,
    ArrowUpTrayIcon,
    CalendarDaysIcon,
    CameraIcon,
    ChatBubbleLeftEllipsisIcon,
    CheckCircleIcon,
    ChevronDownIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EnvelopeIcon,
    ExclamationTriangleIcon,
    GlobeAltIcon,
    StarIcon as OutlineStarIcon,
    PencilSquareIcon,
    PlusCircleIcon,

    TrashIcon,
    UserCircleIcon,
    UserPlusIcon,
    UsersIcon as UsersIconSolid,
    XCircleIcon,
    XMarkIcon
} from "@heroicons/react/24/outline";
import { StarIcon as SolidStarIcon } from "@heroicons/react/24/solid";
import React, {
    useCallback,
    useEffect,
    useRef,
    useState
} from "react";
import {
    FaConciergeBell,
    FaList,
    FaMap,
    FaMapMarkerAlt,
    FaStar,
    FaStarHalfAlt
} from "react-icons/fa";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { Link, useNavigate } from "react-router-dom";
import MapView from "../../components/MapView/MapView";
import EditRecommendationModal from "../../components/Profile/EditRecommendationModal";
import ListCard from "../../components/Profile/ListCard";
import ProfileRecommendationCard from "../../components/Profile/ProfileRecommendationCard";
import ReviewModal from "../../components/ReviewModal/ReviewModal";
import ShareProfileModal from "../../components/ShareProfileModal/ShareProfileModal";
import TrustScoreWheel from "../../components/TrustScoreWheel/TrustScoreWheel";
import { API_URL } from "../../utils/constants";
import { getServiceTagColor } from "../../utils/serviceTagColors";
import "./Profile.css";

// Debounce utility function
const debounce = (func, wait) => {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
};

function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
    );
    return new Promise((resolve, reject) => {
        canvas.toBlob(
            (blob) => {
                if (!blob) {
                    reject(new Error("Canvas is empty"));
                    return;
                }
                blob.name = fileName;
                resolve(blob);
            },
            "image/jpeg",
            0.95
        );
    });
}

const StarRatingDisplay = ({ rating, isProfileCard }) => {
    const numRating = parseFloat(rating) || 0;
    const fullStars = Math.floor(numRating);
    const hasHalf = isProfileCard
        ? numRating - fullStars >= 0.4
        : numRating - fullStars >= 0.5;
    const emptyStars = 5 - fullStars - (hasHalf ? 1 : 0);
    const starClass = isProfileCard ? "profile-star-icon" : "as-star-icon";

    return (
        <div
            className={
                isProfileCard ? "profile-star-display" : "as-star-rating"
            }
        >
            {[...Array(fullStars)].map((_, i) => (
                <FaStar key={`full-${i}`} className={`${starClass} filled`} />
            ))}
            {hasHalf &&
                (isProfileCard ? (
                    <FaStarHalfAlt
                        key="half"
                        className={`${starClass} filled`}
                    />
                ) : (
                    <FaStar
                        key={`half-${Date.now()}-srd`}
                        className={`${starClass} half`}
                    />
                ))}
            {[...Array(emptyStars)].map((_, i) => (
                <FaStar key={`empty-${i}`} className={`${starClass} empty`} />
            ))}
        </div>
    );
};

const EditModalStarDisplay = ({
    active,
    onClick,
    onMouseEnter,
    onMouseLeave,
}) => {
    if (active) {
        return (
            <SolidStarIcon
                className="profile-edit-modal-star-icon filled"
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                aria-hidden="true"
            />
        );
    }
    return (
        <OutlineStarIcon
            className="profile-edit-modal-star-icon"
            onClick={onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            aria-hidden="true"
        />
    );
};

const EDIT_MODAL_PUBLISH_OPTIONS = [
    {
        value: "Full Trust Circle",
        label: "Entire Trust Circle",
        icon: UsersIconSolid,
    },
    {
        value: "Specific Trust Circles",
        label: "Specific Trust Circles",
        icon: UserCircleIcon,
    },
    { value: "Public", label: "Public", icon: GlobeAltIcon },
];

const ImageCarousel = ({ images, onImageClick }) => {
    const [currentIndex, setCurrentIndex] = useState(0);

    if (!images || !Array.isArray(images) || images.length === 0) return null;

    const getImageSrc = (image) => {
        if (!image) return "";

        // Handle new CDN URL structure
        if (image.url) return image.url;

        // Handle GCS path structure (for photo import flow)
        if (image.gcs_path) {
            return `https://storage.googleapis.com/tried-and-trusted-images-production/${image.gcs_path}`;
        }

        // Handle old binary data structure for backward compatibility
        const imageData = image.data?.data || image.data;
        if (!imageData) return "";

        try {
            // Handle array buffer data
            if (Array.isArray(imageData)) {
                const bytes = new Uint8Array(imageData);
                const binary = bytes.reduce(
                    (acc, byte) => acc + String.fromCharCode(byte),
                    ""
                );
                const base64String = window.btoa(binary);
                return `data:${image.contentType};base64,${base64String}`;
            }

            // Handle string data
            if (typeof imageData === "string") {
                return `data:${image.contentType};base64,${imageData}`;
            }

            console.error("Unsupported image data format:", imageData);
            return "";
        } catch (error) {
            console.error("Error converting image data:", error);
            return "";
        }
    };

    return (
        <div className="profile-image-carousel">
            <div className="profile-image-carousel-container">
                <img
                    src={getImageSrc(images[currentIndex])}
                    alt={`Provider image ${currentIndex + 1}`}
                    className="profile-carousel-image"
                    onClick={() => onImageClick?.(images[currentIndex])}
                />
                {images.length > 1 && (
                    <>
                        <button
                            onClick={() =>
                                setCurrentIndex(
                                    (prev) =>
                                        (prev - 1 + images.length) %
                                        images.length
                                )
                            }
                            className="profile-carousel-btn prev"
                        >
                            <ChevronLeftIcon />
                        </button>
                        <button
                            onClick={() =>
                                setCurrentIndex(
                                    (prev) => (prev + 1) % images.length
                                )
                            }
                            className="profile-carousel-btn next"
                        >
                            <ChevronRightIcon />
                        </button>
                    </>
                )}
            </div>
            {images.length > 1 && (
                <div className="profile-carousel-dots">
                    {images.map((_, idx) => (
                        <button
                            key={idx}
                            className={`profile-carousel-dot ${
                                idx === currentIndex ? "active" : ""
                            }`}
                            onClick={() => setCurrentIndex(idx)}
                        />
                    ))}
                </div>
            )}
            {/* {isReviewModalOpen && providerForReview && (
                <ReviewModal
                    isOpen={isReviewModalOpen}
                    onClose={handleCloseReviewModal}
                    onSubmit={handleSubmitReview}
                    provider={providerForReview}
                />
            )} */}
        </div>
    );
};

const ImageModal = ({ image, onClose }) => {
    const getImageSrc = (img) => {
        if (!img) return "";

        // Handle new CDN URL structure
        if (img.url) return img.url;

        // Handle GCS path structure (for photo import flow)
        if (img.gcs_path) {
            return `https://storage.googleapis.com/tried-and-trusted-images-production/${img.gcs_path}`;
        }

        // Handle old binary data structure for backward compatibility
        const imageData = img.data?.data || img.data;
        if (!imageData) return "";

        try {
            // Handle array buffer data
            if (Array.isArray(imageData)) {
                const bytes = new Uint8Array(imageData);
                const binary = bytes.reduce(
                    (acc, byte) => acc + String.fromCharCode(byte),
                    ""
                );
                const base64String = window.btoa(binary);
                return `data:${img.contentType};base64,${base64String}`;
            }

            // Handle string data
            if (typeof imageData === "string") {
                return `data:${img.contentType};base64,${imageData}`;
            }

            console.error("Unsupported image data format:", imageData);
            return "";
        } catch (error) {
            console.error("Error converting image data:", error);
            return "";
        }
    };

    return (
        <div className="profile-image-modal-overlay" onClick={onClose}>
            <div
                className="profile-image-modal-content"
                onClick={(e) => e.stopPropagation()}
            >
                <button className="profile-image-modal-close" onClick={onClose}>
                    <XMarkIcon />
                </button>
                <img
                    src={getImageSrc(image)}
                    alt="Full size provider image"
                    className="profile-image-modal-img"
                    onError={(e) => {
                        console.error("Modal image load error:", e);
                        e.target.style.display = "none";
                    }}
                />
            </div>
        </div>
    );
};

const MyRecommendationCard = ({
    rec,
    onEdit,
    onLikeRecommendation,
    onRefreshList,
    user,
}) => {
    const providerIdForLink = rec.provider_id || rec.id;
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);
    const cardRef = useRef(null);
    const formatDate = (dateString) =>
        !dateString
            ? "Date not available"
            : new Date(dateString).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
              });
    const handleShare = async () => {
        const providerIdForLink = rec.provider_id || rec.id;
        const url = `${window.location.origin}/provider/${providerIdForLink}`;

        try {
            await navigator.clipboard.writeText(url);

            // Show a temporary success message on button
            const button = document.querySelector(
                ".profile-my-rec-dropdown-menu .profile-my-rec-dropdown-item:nth-child(2)"
            );
            if (button) {
                const originalContent = button.innerHTML;
                button.innerHTML =
                    '<svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg> Link Copied!';
                button.style.color = "#28a745";
                setTimeout(() => {
                    button.innerHTML = originalContent;
                    button.style.color = "";
                }, 2000);
            }

            // Show a prominent success notification
            const notification = document.createElement("div");
            notification.innerHTML = `
                <div style="
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    background: #28a745;
                    color: white;
                    padding: 12px 20px;
                    border-radius: 8px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    z-index: 10000;
                    font-family: 'Inter', sans-serif;
                    font-size: 14px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    animation: slideInFromRight 0.3s ease-out;
                ">
                    <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                    </svg>
                    Recommendation link copied to clipboard!
                </div>
            `;

            // Add CSS animation if not already present
            if (!document.querySelector("#copy-link-animation-styles")) {
                const style = document.createElement("style");
                style.id = "copy-link-animation-styles";
                style.textContent = `
                    @keyframes slideInFromRight {
                        from {
                            opacity: 0;
                            transform: translateX(100%);
                        }
                        to {
                            opacity: 1;
                            transform: translateX(0);
                        }
                    }
                `;
                document.head.appendChild(style);
            }

            document.body.appendChild(notification);

            // Remove notification after 3 seconds
            setTimeout(() => {
                if (notification.parentNode) {
                    notification.style.animation =
                        "slideInFromRight 0.3s ease-out reverse";
                    setTimeout(() => {
                        if (notification.parentNode) {
                            notification.parentNode.removeChild(notification);
                        }
                    }, 300);
                }
            }, 3000);
        } catch (err) {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement("textarea");
            textArea.value = url;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand("copy");

                // Show success notification for fallback method too
                const notification = document.createElement("div");
                notification.innerHTML = `
                    <div style="
                        position: fixed;
                        top: 20px;
                        right: 20px;
                        background: #28a745;
                        color: white;
                        padding: 12px 20px;
                        border-radius: 8px;
                        box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                        z-index: 10000;
                        font-family: 'Inter', sans-serif;
                        font-size: 14px;
                        font-weight: 500;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                    ">
                        <svg width="16" height="16" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                        </svg>
                        Recommendation link copied to clipboard!
                    </div>
                `;
                document.body.appendChild(notification);
                setTimeout(() => {
                    if (notification.parentNode) {
                        notification.parentNode.removeChild(notification);
                    }
                }, 3000);

                //console.log("Link copied to clipboard (fallback)");
            } catch (fallbackErr) {
                console.error("Failed to copy link:", fallbackErr);
                alert(`Copy this link: ${url}`);
            }
            document.body.removeChild(textArea);
        }
        setDropdownOpen(false);
    };
    const displayCommunityAvgRatingVal = (
        parseFloat(rec.average_rating) || 0
    ).toFixed(1);
    const displayTotalReviewsVal = parseInt(rec.total_reviews, 10) || 0;

    const handleLikeClick = () => {
        const providerIdToLike = rec.provider_id || rec.id;
        if (providerIdToLike && onLikeRecommendation) {
            onLikeRecommendation(providerIdToLike);
        } else {
            console.warn(
                "Provider ID is missing for like action on recommendation card.",
                rec
            );
        }
    };

    const handleDeleteClick = () => {
        setShowDeleteModal(true);
    };

    const handleDelete = async (e) => {
        e.preventDefault();
        setIsDeleting(true);
        setDeleteError(null);

        try {
            const response = await fetch(
                `${API_URL}/api/recommendations/${rec.id}?user_id=${user?.id}&email=${user?.emailAddresses[0].emailAddress}`,
                {
                    method: "DELETE",
                }
            );

            if (!response.ok) {
                throw new Error("Failed to delete recommendation");
            }

            setShowDeleteModal(false);
            setTimeout(() => {
                onRefreshList(); // Add this prop to handle list refresh
            }, 500);
        } catch (error) {
            setDeleteError(
                "Failed to delete recommendation. Please try again."
            );
            //console.log("Error deleting recommendation:", error);
            setIsDeleting(false);
        }
    };

    // Parse images if they're stored as a string
    const parsedImages = React.useMemo(() => {
        if (!rec.images) return [];
        if (Array.isArray(rec.images)) return rec.images;
        try {
            return JSON.parse(rec.images);
        } catch (e) {
            console.error("Error parsing images:", e);
            return [];
        }
    }, [rec.images]);

    return (
        <li className="profile-my-rec-card">
            <div className="profile-my-rec-card-header">
                <h2 className="profile-my-rec-card-title">
                    <Link
                        to={`/provider/${providerIdForLink}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="clickable provider-name-link"
                                                    onClick={() => {
                                const essentialProviderData = {
                                    id: rec.id,
                                    provider_id: rec.provider_id,
                                    business_name: rec.business_name,
                                    recommender_message: rec.recommender_message,
                                    rating: rec.rating,
                                    average_rating: rec.average_rating,
                                    total_reviews: rec.total_reviews,
                                    tags: rec.tags,
                                    provider_contact_name:
                                        rec.provider_contact_name,
                                    website: rec.website,
                                    phone_number: rec.phone_number,
                                    street_address: rec.street_address,
                                    city: rec.city,
                                    state: rec.state,
                                    zip_code: rec.zip_code,
                                    recommended_service_name:
                                        rec.recommended_service_name,
                                    date_of_recommendation:
                                        rec.date_of_recommendation,
                                    created_at: rec.created_at,
                                };
                                localStorage.setItem(
                                    "selectedProvider",
                                    JSON.stringify(essentialProviderData)
                                );
                            }}
                    >
                        {rec.business_name || "Unknown Business"}
                    </Link>
                </h2>
                <div className="profile-my-rec-badge-wrapper-with-menu">
                    <div className="profile-my-rec-badge-group">
                        {/* Top Rated badge logic - commented out as requested, but keeping for potential future use */}
                        {/* {(parseFloat(rec.average_rating) || 0) >= 4.5 && (
                            <span className="profile-my-rec-top-rated-badge">
                                Top Rated
                            </span>
                        )} */}
                        {rec.recommended_service_name && (
                            <span className="profile-my-rec-service-name-badge" style={{ backgroundColor: getServiceTagColor(rec.recommended_service_name).bg, color: getServiceTagColor(rec.recommended_service_name).text }}>
                                {rec.recommended_service_name}
                            </span>
                        )}
                    </div>
                    <div className="profile-my-rec-right-actions">
                        <div className="profile-my-rec-dropdown-wrapper">
                            <button
                                className="profile-my-rec-three-dots-button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                title="Options"
                            >
                                ⋮
                            </button>
                            {dropdownOpen && (
                                <div className="profile-my-rec-dropdown-menu">
                                    <button
                                        className="profile-my-rec-dropdown-item"
                                        onClick={() => {
                                            onEdit(rec);
                                            setDropdownOpen(false);
                                        }}
                                    >
                                        <PencilSquareIcon /> Edit Recommendation
                                    </button>
                                    <button
                                        className="profile-my-rec-dropdown-item"
                                        onClick={handleShare}
                                    >
                                        <ShareIcon /> Share Recommendation
                                    </button>
                                    <button
                                        className="profile-my-rec-dropdown-item delete-action"
                                        onClick={() => {
                                            handleDeleteClick();
                                            setDropdownOpen(false);
                                        }}
                                    >
                                        <TrashIcon />{" "}
                                        <span className="delete-text">
                                            Delete Recommendation
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            {(rec.city || rec.state) && (
                <div className="profile-my-rec-location-info">
                    <FaMapMarkerAlt />
                    <span>
                        {rec.city}
                        {rec.city && rec.state && ", "}
                        {rec.state}
                    </span>
                </div>
            )}
            <div className="profile-my-rec-review-summary">
                {typeof rec.average_rating === "number" ||
                rec.average_rating === 0 ? (
                    <>
                        <StarRatingDisplay
                            rating={rec.average_rating || 0}
                            isProfileCard={false}
                        />
                        <span className="profile-my-rec-rating-score-text">
                            {displayCommunityAvgRatingVal} (
                            {displayTotalReviewsVal} Reviews)
                        </span>
                    </>
                ) : (
                    <>
                        <StarRatingDisplay rating={0} isProfileCard={false} />
                        <span className="profile-my-rec-rating-score-text">
                            0.0 (0 Reviews)
                        </span>
                    </>
                )}
                {/* <button
                    className={`profile-my-rec-like-button ${rec.currentUserLiked ? 'liked' : ''}`}
                    onClick={handleLikeClick}
                    title={rec.currentUserLiked ? "Unlike this provider" : "Like this provider"}
                >
                    <FaThumbsUp />
                    <span className="profile-my-rec-like-count">{rec.num_likes || 0}</span>
                </button> */}
            </div>
            <p className="profile-my-rec-card-description">
                <ChatBubbleLeftEllipsisIcon className="inline-icon" />
                {rec.recommender_message || "No detailed message provided."}
            </p>
            {rec.users_who_reviewed &&
                rec.users_who_reviewed.length > 0 &&
                rec.users_who_reviewed.filter((name) => {
                    if (!name) return false;
                    // Exclude the current user's name
                    const currentUserName =
                        user?.firstName && user?.lastName
                            ? `${user.firstName} ${user.lastName}`.trim()
                            : user?.firstName || user?.lastName || "";
                    return name !== currentUserName;
                }).length > 0 && (
                    <div className="recommended-row">
                        <span className="recommended-label">Also used by:</span>
                        <span className="used-by-names">
                            {rec.users_who_reviewed
                                .filter((name) => {
                                    if (!name) return false;
                                    const currentUserName =
                                        user?.firstName && user?.lastName
                                            ? `${user.firstName} ${user.lastName}`.trim()
                                            : user?.firstName ||
                                              user?.lastName ||
                                              "";
                                    return name !== currentUserName;
                                })
                                .join(", ")}
                        </span>
                    </div>
                )}
            {parsedImages.length > 0 && (
                <div className="profile-my-rec-images">
                    <ImageCarousel
                        images={parsedImages}
                        onImageClick={(image) => setSelectedImage(image)}
                    />
                </div>
            )}
            {Array.isArray(rec.tags) && rec.tags.length > 0 && (
                <div className="profile-my-rec-tag-container">
                    {rec.tags.map((tag, idx) => (
                        <span key={idx} className="profile-my-rec-tag-badge">
                            {tag}
                        </span>
                    ))}
                    <button
                        className="profile-my-rec-add-tag-button"
                        onClick={() => onEdit(rec)}
                        aria-label="Edit tags"
                    >
                        +
                    </button>
                </div>
            )}
            {Array.isArray(rec.tags) && rec.tags.length === 0 && (
                <div className="profile-my-rec-tag-container">
                    <span className="profile-my-rec-no-tags-text">
                        No tags.
                    </span>
                    <button
                        className="profile-my-rec-add-tag-button"
                        onClick={() => onEdit(rec)}
                        aria-label="Add a tag"
                    >
                        +
                    </button>
                </div>
            )}
            <div className="profile-my-rec-card-footer">
                <div className="profile-my-rec-date">
                    <CalendarDaysIcon className="inline-icon" />
                    Recommended on:{" "}
                    {formatDate(rec.date_of_recommendation || rec.created_at)}
                </div>
                <div className="profile-my-rec-action-buttons">
                    <button
                        className="profile-my-rec-primary-action-button"
                        onClick={() => onEdit(rec)}
                    >
                        <PencilSquareIcon className="btn-icon" /> Edit My Rec
                    </button>
                </div>
            </div>
            {selectedImage && (
                <ImageModal
                    image={selectedImage}
                    onClose={() => setSelectedImage(null)}
                />
            )}
            {showDeleteModal && (
                <div className="profile-edit-modal-overlay">
                    <div className="profile-edit-modal-content">
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            className="profile-edit-modal-close-btn"
                        >
                            &times;
                        </button>
                        <h2 className="profile-edit-modal-title">
                            Delete Recommendation
                        </h2>
                        <div className="profile-delete-modal-body">
                            <div className="profile-delete-modal-warning">
                                <ExclamationTriangleIcon className="warning-icon" />
                                <p>
                                    Are you sure you want to delete this
                                    recommendation? This action cannot be
                                    undone.
                                </p>
                            </div>
                            {deleteError && (
                                <div className="profile-edit-modal-message error">
                                    <XCircleIcon />
                                    <span>{deleteError}</span>
                                </div>
                            )}
                            <div className="profile-edit-modal-button-row">
                                <button
                                    className="profile-edit-modal-btn cancel-btn"
                                    onClick={() => setShowDeleteModal(false)}
                                    disabled={isDeleting}
                                >
                                    <XCircleIcon /> Cancel
                                </button>
                                <button
                                    className="profile-edit-modal-btn delete-btn"
                                    onClick={handleDelete}
                                    disabled={isDeleting}
                                >
                                    {isDeleting ? (
                                        <>
                                            <ArrowPathIcon className="animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <TrashIcon />
                                            Delete
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </li>
    );
};

const AchievementBadge = ({ recCount, onStartRecommending }) => {
    const getBadgeInfo = (count) => {
        if (count >= 100) {
            return {
                level: "Diamond",
                tier: "Diamond Recommender",
                className: "achievement-badge-diamond",
                icon: "💎",
                description: `Diamond Recommender (${count} recommendations)`,
            };
        } else if (count >= 50) {
            return {
                level: "Platinum",
                tier: "Platinum Recommender",
                className: "achievement-badge-platinum",
                icon: "⭐",
                description: `Platinum Recommender (${count} recommendations)`,
            };
        } else if (count >= 25) {
            return {
                level: "Gold",
                tier: "Gold Recommender",
                className: "achievement-badge-gold",
                icon: "🏆",
                description: `Gold Recommender (${count} recommendations)`,
            };
        } else if (count >= 10) {
            return {
                level: "Silver",
                tier: "Silver Recommender",
                className: "achievement-badge-silver",
                icon: "🥈",
                description: `Silver Recommender (${count} recommendations)`,
            };
        } else if (count >= 1) {
            return {
                level: "Bronze",
                tier: "Bronze Recommender",
                className: "achievement-badge-bronze",
                icon: "🥉",
                description: `Bronze Recommender (${count} recommendations)`,
            };
        }
        return null;
    };

    const badge = getBadgeInfo(recCount);

    if (!badge) {
        // Show incentivizing starter badge for users with 0 recommendations
        return (
            <div
                className="achievement-badge-starter"
                title="Share your first recommendation to unlock Bronze tier!"
                onClick={onStartRecommending}
            >
                <div className="achievement-badge-icon">🌟</div>
            </div>
        );
    }

    return (
        <div
            className={`achievement-badge-circular ${badge.className}`}
            title={badge.description}
        >
            <div className="achievement-badge-icon">{badge.icon}</div>
        </div>
    );
};

function UserListCard({ list, fetchListRecommendations }) {
    const [expanded, setExpanded] = useState(false);
    const [recs, setRecs] = useState([]);
    const [loading, setLoading] = useState(false);

    const handleExpand = async () => {
        setExpanded((prev) => !prev);
        if (!expanded && recs.length === 0) {
            setLoading(true);
            const recommendations = await fetchListRecommendations(list.id);
            setRecs(recommendations);
            setLoading(false);
        }
    };

    return (
        <li className="profile-user-list-card">
            <div
                className="profile-user-list-header"
                onClick={handleExpand}
                style={{ cursor: "pointer" }}
            >
                <h3>{list.title}</h3>
                <span className="profile-user-list-date">
                    {new Date(list.created_at).toLocaleDateString()}
                </span>
                <ChevronDownIcon
                    className={`profile-user-list-chevron${
                        expanded ? " rotated" : ""
                    }`}
                />
            </div>
            <p className="profile-user-list-description">{list.description}</p>
            {expanded && (
                <div className="profile-user-list-recommendations">
                    {loading ? (
                        <div>Loading recommendations...</div>
                    ) : recs.length === 0 ? (
                        <div>No recommendations in this list.</div>
                    ) : (
                        <ul className="profile-user-list-recs">
                            {recs.map((rec) => (
                                <li
                                    key={rec.id}
                                    className="profile-user-list-rec-card"
                                >
                                    <div className="profile-user-list-rec-header">
                                        <span className="profile-user-list-rec-title">
                                            {rec.business_name}
                                        </span>
                                        <StarRatingDisplay
                                            rating={
                                                rec.rating ||
                                                rec.initial_rating ||
                                                0
                                            }
                                            isProfileCard={false}
                                        />
                                    </div>
                                    <div className="profile-user-list-rec-details">
                                        <span>{rec.recommender_message}</span>
                                        {Array.isArray(rec.tags) &&
                                            rec.tags.length > 0 && (
                                                <div className="profile-user-list-rec-tags">
                                                    {rec.tags.map(
                                                        (tag, idx) => (
                                                            <span
                                                                key={idx}
                                                                className="profile-user-list-rec-tag"
                                                            >
                                                                {tag}
                                                            </span>
                                                        )
                                                    )}
                                                </div>
                                            )}
                                        {rec.images &&
                                            rec.images.length > 0 && (
                                                <div className="profile-user-list-rec-images">
                                                    <img
                                                        src={
                                                            typeof rec
                                                                .images[0] ===
                                                            "string"
                                                                ? rec.images[0]
                                                                : rec.images[0]
                                                                      ?.preview ||
                                                                  ""
                                                        }
                                                        alt="Preview"
                                                        className="profile-user-list-rec-image"
                                                        style={{
                                                            maxWidth: "80px",
                                                            maxHeight: "80px",
                                                            borderRadius: "8px",
                                                        }}
                                                    />
                                                    {rec.images.length > 1 && (
                                                        <span className="profile-user-list-rec-image-count">
                                                            +
                                                            {rec.images.length -
                                                                1}{" "}
                                                            more
                                                        </span>
                                                    )}
                                                </div>
                                            )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </li>
    );
}

const Profile = () => {
    const { isLoaded, isSignedIn, user } = useUser();
    const { signOut } = useClerk();
    const navigate = useNavigate();
    const [baseRecommendations, setBaseRecommendations] = useState([]);
    const [enrichedRecommendations, setEnrichedRecommendations] = useState([]);
    const [connections, setConnections] = useState([]);
    const [profileUserData, setProfileUserData] = useState(null);
    const [userBio, setUserBio] = useState("");
    const [profileImage, setProfileImage] = useState("");
    const [editingBio, setEditingBio] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [imgSrcForCropper, setImgSrcForCropper] = useState("");
    const imgRef = useRef(null);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [originalFile, setOriginalFile] = useState(null);
    const [sortOption, setSortOption] = useState("date_of_recommendation");
    const [loading, setLoading] = useState(true);
    const [statsLoading, setStatsLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [currentEditingRec, setCurrentEditingRec] = useState(null);
    const [userTrustCircles, setUserTrustCircles] = useState([]);
    const [trustCirclesLoading, setTrustCirclesLoading] = useState(false);
    const [trustCirclesError, setTrustCirclesError] = useState("");
    const [likedRecommendations, setLikedRecommendations] = useState(new Set());
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [selectedCities, setSelectedCities] = useState([]);
    const [showCityFilter, setShowCityFilter] = useState(false);
    const [selectedServices, setSelectedServices] = useState([]);
    const [showServiceFilter, setShowServiceFilter] = useState(false);
    const [userScore, setUserScore] = useState(0);
    // Review modal state for writing reviews
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [providerForReview, setProviderForReview] = useState(null);
    // Batch comments state
    const [commentsMap, setCommentsMap] = useState(new Map());
    const [isLoadingComments, setIsLoadingComments] = useState(false);
    const [currentUserName, setCurrentUserName] = useState(null);
    const ASPECT_RATIO = 1;
    const MIN_DIMENSION = 150;

    const [userLists, setUserLists] = useState([]);
    const [listsLoading, setListsLoading] = useState(false);
    const [listsError, setListsError] = useState("");

    const [expandedListId, setExpandedListId] = useState(null);
    const [listRecs, setListRecs] = useState({});
    const [listRecsLoading, setListRecsLoading] = useState({});

    const [viewMode, setViewMode] = useState("list"); // 'list' or 'map'
    const [userLocation, setUserLocation] = useState(null);

    // Add state for server-side filtering
    const [availableServices, setAvailableServices] = useState([]);
    const [availableCities, setAvailableCities] = useState([]);
    const [serviceIdCounts, setServiceIdCounts] = useState({});
    const [isLoadingFilters, setIsLoadingFilters] = useState(false);
    const [totalRecommendationCount, setTotalRecommendationCount] = useState(0);

    // Cache for username to avoid repeated calls
    const [cachedUsername, setCachedUsername] = useState(null);
    const [hasInitialized, setHasInitialized] = useState(false);

    // Invite functionality state
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
    const [inviteLoading, setInviteLoading] = useState(false);
    const [inviteError, setInviteError] = useState(null);
    const [inviteSuccess, setInviteSuccess] = useState(null);

    const getClerkUserQueryParams = useCallback(() => {
        if (!user) return "";
        return new URLSearchParams({
            user_id: user.id,
            email: user.primaryEmailAddress?.emailAddress,
            firstName: user.firstName || "",
            lastName: user.lastName || "",
            phoneNumber: user.primaryPhoneNumber?.phoneNumber || "",
        }).toString();
    }, [user]);


    // Invite functionality
    const handleGenerateInvite = async () => {
        if (!user?.id) {
            setInviteError("User not authenticated");
            return;
        }

        setInviteLoading(true);
        setInviteError(null);
        setInviteSuccess(null);

        try {
            const response = await fetch(`${API_URL}/api/invites/platform/invites`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    actingUserClerkId: user.id,
                    expires_at: null, // No expiration
                    max_uses: null, // No limit on uses
                }),
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to generate invite');
            }

            const result = await response.json();
            setInviteSuccess(result.invite_url);
            setIsInviteModalOpen(true);
        } catch (error) {
            setInviteError(error.message || 'Failed to generate invite');
        } finally {
            setInviteLoading(false);
        }
    };

    const handleCopyInviteLink = async () => {
        if (!inviteSuccess) return;

        try {
            await navigator.clipboard.writeText(inviteSuccess);
            
            // Show temporary success message
            const originalText = document.querySelector('.invite-success-message')?.textContent;
            if (originalText) {
                const successElement = document.querySelector('.invite-success-message');
                successElement.textContent = 'Link copied to clipboard!';
                successElement.style.color = '#28a745';
                
                setTimeout(() => {
                    successElement.textContent = originalText;
                    successElement.style.color = '';
                }, 2000);
            }
        } catch (err) {
            // Fallback for browsers that don't support clipboard API
            const textArea = document.createElement("textarea");
            textArea.value = inviteSuccess;
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand("copy");
                alert("Invite link copied to clipboard!");
            } catch (fallbackErr) {
                console.error("Failed to copy invite link:", fallbackErr);
                alert(`Copy this invite link: ${inviteSuccess}`);
            }
            document.body.removeChild(textArea);
        }
    };

    // Consolidated data fetching function - replaces multiple separate functions
    const fetchAllProfileData = useCallback(async (username, forceRefresh = false, isInitialLoad = false) => {
        console.log('fetchAllProfileData called with:', { username, forceRefresh, isInitialLoad, loading });
        
        if (!isLoaded || !isSignedIn || !user || !username) {
            console.log('fetchAllProfileData early return - conditions not met:', {
                isLoaded, isSignedIn, hasUser: !!user, hasUsername: !!username
            });
            return;
        }
        
        // Prevent multiple simultaneous calls
        if (loading && !forceRefresh) {
            console.log('fetchAllProfileData skipped - already loading');
            return;
        }
        
        console.log('Starting fetchAllProfileData...');
        setError(null);
        
        try {
            // Build query parameters for server-side filtering
            const params = new URLSearchParams();
            params.set('loggedInUserId', user.id);
            
            // Add filter parameters
            if (selectedServices.length > 0) {
                params.set('services', selectedServices.join(','));
            }
            if (selectedCities.length > 0) {
                params.set('cities', selectedCities.join(','));
            }
            
            console.log('Fetching all data in parallel...');
            
            // Only fetch connections and user score during initial load
            const promises = [];
            
            // Profile data with recommendations (only fetch if filters are applied)
            if (selectedServices.length > 0 || selectedCities.length > 0) {
                promises.push(fetch(`${API_URL}/api/users/pro/${username}?${params.toString()}`));
            } else {
                promises.push(Promise.resolve({ ok: false }));
            }
            
            // Connections/followers - only fetch during initial load
            if (isInitialLoad) {
                promises.push(fetch(`${API_URL}/api/connections/followers?user_id=${user.id}`));
            } else {
                promises.push(Promise.resolve({ ok: false }));
            }
            
            // User score - only fetch during initial load
            if (isInitialLoad) {
                promises.push(fetch(`${API_URL}/api/users/preferred-name?email=${encodeURIComponent(
                    user.primaryEmailAddress.emailAddress
                )}`));
            } else {
                promises.push(Promise.resolve({ ok: false }));
            }
            
            // Filter options (always fetch on initial load or when forced)
            if (forceRefresh || (selectedServices.length > 0 || selectedCities.length > 0)) {
                promises.push(fetch(`${API_URL}/api/users/pro/${username}/filter-options?${params.toString()}`));
            } else if (availableServices.length === 0) {
                promises.push(fetch(`${API_URL}/api/users/pro/${username}/filter-options?${params.toString()}`));
            } else {
                promises.push(Promise.resolve({ ok: false }));
            }
            
            // User lists - only fetch during initial load
            if (isInitialLoad) {
                promises.push(fetch(`${API_URL}/api/lists?user_id=${user.id}&email=${encodeURIComponent(
                    user.primaryEmailAddress.emailAddress
                )}`));
            } else {
                promises.push(Promise.resolve({ ok: false }));
            }
            
            const [profileRes, connectionsRes, scoreRes, filterOptionsRes, listsRes] = await Promise.all(promises);
            
            console.log('All API calls completed, processing responses...');
            
            // Process profile data (only if filters are applied)
            if ((selectedServices.length > 0 || selectedCities.length > 0) && profileRes.ok) {
                const profileData = await profileRes.json();
                console.log('Filtered profile data received:', { 
                    hasProfileData: !!profileData,
                    recommendationsCount: profileData.recommendations?.length || 0
                });
                
                // Process filtered recommendations
                const filteredRecs = (profileData.recommendations || []).map(rec => ({
                    ...rec,
                    average_rating: parseFloat(rec.average_rating) || 0,
                    total_reviews: parseInt(rec.total_reviews, 10) || 0,
                    users_who_reviewed: rec.users_who_reviewed || []
                }));
                
                setBaseRecommendations(filteredRecs);
            } else if (selectedServices.length === 0 && selectedCities.length === 0) {
                // When no filters are applied, keep the original recommendations
                // (this will be handled by the fetchUsername function)
                console.log('No filters applied, keeping original recommendations');
            }
            
            // Process connections (only during initial load)
            if (isInitialLoad) {
                if (!connectionsRes.ok) {
                    setConnections([]);
                } else {
                    const connsData = await connectionsRes.json();
                    console.log('Followers data:', connsData);
                    setConnections(Array.isArray(connsData) ? connsData : []);
                }
            }
            
            // Process user score (only during initial load)
            if (isInitialLoad) {
                if (scoreRes.ok) {
                    const scoreData = await scoreRes.json();
                    console.log('User score data:', scoreData);
                    const score = parseInt(scoreData.userScore || scoreData.user_score) || 0;
                    console.log('Parsed user score:', score);
                    setUserScore(score);
                } else {
                    console.log('User score response not ok:', scoreRes.status);
                    setUserScore(0);
                }
            }
            
            // Process filter options (if fetched)
            if (filterOptionsRes.ok) {
                const filterData = await filterOptionsRes.json();
                console.log('Filter options data:', filterData);
                if (filterData.success) {
                    setAvailableServices(filterData.services || []);
                    setAvailableCities(filterData.cities || []);
                    setServiceIdCounts(filterData.serviceIdCounts || {});
                }
            } else {
                console.log('Filter options response not ok:', filterOptionsRes.status);
            }
            
            // Process user lists (only during initial load)
            if (isInitialLoad) {
                if (listsRes.ok) {
                    const listsData = await listsRes.json();
                    if (listsData.success) {
                        setUserLists(listsData.lists || []);
                        setListsError("");
                    } else {
                        setListsError(listsData.message || "Failed to fetch lists");
                        setUserLists([]);
                    }
                } else {
                    setListsError("Could not load your lists.");
                    setUserLists([]);
                }
            }
            
            console.log('fetchAllProfileData completed successfully');
            
        } catch (err) {
            console.error('fetchAllProfileData failed:', err);
            setError(err.message);
        }
    }, [
        isLoaded, 
        isSignedIn, 
        user, 
        selectedServices, 
        selectedCities, 
        availableServices.length,
        loading
    ]);

    // Fetch username first - simple and reliable
    const fetchUsername = useCallback(async () => {
        console.log('fetchUsername called with:', { isLoaded, isSignedIn, user: !!user });
        
        if (!isLoaded || !isSignedIn || !user) {
            console.log('fetchUsername early return - conditions not met');
            return null;
        }
        
        try {
            const queryParams = getClerkUserQueryParams();
            console.log('Query params:', queryParams);
            
            if (!queryParams) {
                console.error('No query params available');
                throw new Error("User details not available.");
            }
            
            const url = `${API_URL}/api/users/me/recommendations?${queryParams}`;
            console.log('Fetching username from:', url);
            
            const userInfoRes = await fetch(url);
            console.log('Username response status:', userInfoRes.status);
            
            if (!userInfoRes.ok) {
                const errorText = await userInfoRes.text();
                console.error('Username fetch failed:', errorText);
                throw new Error("Failed to fetch user info");
            }
            
            const userInfoData = await userInfoRes.json();
            console.log('Username response data:', userInfoData);
            
            const username = userInfoData.userUsername;
            console.log('Extracted username:', username);
            
            // Use the data from the username response directly
            if (userInfoData.success) {
                console.log('Setting profile data from username response');
                setProfileUserData({
                    userId: userInfoData.userId,
                    userName: userInfoData.userName,
                    userUsername: username,
                    userPhone: userInfoData.userPhone,
                    userEmail: userInfoData.userEmail,
                    userBio: userInfoData.userBio,
                    profileImage: userInfoData.profileImage,
                    recommendations: userInfoData.recommendations
                });
                
                // Process recommendations with enrichment logic
                const enrichedRecs = (userInfoData.recommendations || []).map(rec => ({
                    ...rec,
                    average_rating: parseFloat(rec.average_rating) || 0,
                    total_reviews: parseInt(rec.total_reviews, 10) || 0,
                    users_who_reviewed: rec.users_who_reviewed || []
                }));
                
                setBaseRecommendations(enrichedRecs);
                
                // Set total count to the user's total number of recommendations (not affected by filters)
                const totalCount = userInfoData.pagination?.totalCount || enrichedRecs.length;
                console.log('Setting totalRecommendationCount to:', totalCount);
                setTotalRecommendationCount(totalCount);
                
                setUserBio(userInfoData.userBio || "");
                setEditingBio(userInfoData.userBio || "");
                setProfileImage(userInfoData.profileImage || "");
                
                // Set loading to false since we have the data
                setLoading(false);
                setHasInitialized(true);
                
                // Now fetch additional data (followers, user score, etc.)
                if (username) {
                    await fetchAllProfileData(username, true, true);
                }
            }
            
            return username;
        } catch (error) {
            console.error('Error fetching username:', error);
            return null;
        }
    }, [isLoaded, isSignedIn, user, getClerkUserQueryParams, selectedServices, selectedCities, fetchAllProfileData]);

    // Debounced filter options fetch - only for filter changes
    const debouncedFetchFilterOptions = useCallback(
        debounce(async (username, userId, services, cities) => {
            if (!username || !userId) return;
            
            try {
                setIsLoadingFilters(true);
                const params = new URLSearchParams();
                params.set('loggedInUserId', userId);
                
                if (services.length > 0) {
                    params.set('services', services.join(','));
                }
                if (cities.length > 0) {
                    params.set('cities', cities.join(','));
                }
                
                const response = await fetch(
                    `${API_URL}/api/users/pro/${username}/filter-options?${params.toString()}`
                );
                if (!response.ok) {
                    throw new Error('Failed to fetch filter options');
                }
                
                const data = await response.json();
                if (data.success) {
                    setAvailableServices(data.services || []);
                    setAvailableCities(data.cities || []);
                    setServiceIdCounts(data.serviceIdCounts || {});
                }
            } catch (error) {
                console.error('Error fetching filter options:', error);
            } finally {
                setIsLoadingFilters(false);
            }
        }, 300),
        []
    );

    // Single useEffect for initial data fetch - fetch username first, then data
    useEffect(() => {
        console.log('Profile useEffect triggered:', {
            isSignedIn,
            hasUser: !!user,
            hasInitialized,
            isLoaded,
            loading
        });
        
        const initializeProfile = async () => {
            console.log('Profile initialization:', { isSignedIn, user: !!user, hasInitialized, isLoaded });
            
            if (isSignedIn && user && !hasInitialized) {
                console.log('Starting profile initialization...');
                setLoading(true); // Ensure loading is set to true
                
                try {
                    const username = await fetchUsername();
                    console.log('Fetched username:', username);
                    
                    if (!username) {
                        console.error('Failed to fetch username');
                        setError('Failed to load user profile');
                        setLoading(false);
                    }
                    // Note: fetchUsername now handles setting the profile data
                } catch (error) {
                    console.error('Error during profile initialization:', error);
                    setError('Failed to load profile data');
                    setLoading(false);
                }
            } else if (isLoaded && !isSignedIn) {
                console.log('User not signed in, navigating to home');
                navigate("/");
            } else {
                console.log('Profile initialization skipped:', {
                    isSignedIn,
                    hasUser: !!user,
                    hasInitialized,
                    isLoaded
                });
            }
        };
        
        initializeProfile();
    }, [isSignedIn, user, isLoaded, fetchUsername, hasInitialized, navigate]);

    // Single useEffect for filter changes
    useEffect(() => {
        const handleFilterChange = async () => {
            if (hasInitialized && profileUserData?.userUsername && user) {
                // Refetch data with new filters (but not connections/user score)
                await fetchAllProfileData(profileUserData.userUsername, true, false);
            }
        };
        
        handleFilterChange();
    }, [selectedServices, selectedCities, hasInitialized, user, profileUserData?.userUsername, fetchAllProfileData]);

    // Process recommendations when base data changes
    useEffect(() => {
        if (baseRecommendations.length > 0 && user?.id) {
            setStatsLoading(true);
            
            const enriched = baseRecommendations.map((rec) => ({
                ...rec,
                average_rating: parseFloat(rec.average_rating) || 0,
                total_reviews: parseInt(rec.total_reviews, 10) || 0,
                num_likes: parseInt(rec.num_likes, 10) || 0,
                currentUserLiked: rec.currentUserLiked || false,
                users_who_reviewed: rec.users_who_reviewed || [],
            }));

            setEnrichedRecommendations(enriched);

            const newInitialUserLikes = new Set();
            enriched.forEach((r) => {
                const providerId = r.provider_id || r.id;
                if (r.currentUserLiked && providerId) {
                    newInitialUserLikes.add(providerId);
                }
            });
            setLikedRecommendations(newInitialUserLikes);

            setStatsLoading(false);
        } else {
            setEnrichedRecommendations([]);
            setLikedRecommendations(new Set());
            if (!loading) setStatsLoading(false);
        }
    }, [baseRecommendations, loading, user]);

    // Set current user name when user loads
    useEffect(() => {
        if (isLoaded && isSignedIn && user) {
            setCurrentUserName(user.firstName || user.lastName || "User");
        } else if (isLoaded && !isSignedIn) {
            setCurrentUserName(null);
        }
    }, [isLoaded, isSignedIn, user]);

    // Fetch comments for recommendations
    useEffect(() => {
        if (!loading && !statsLoading && enrichedRecommendations.length > 0) {
            fetchBatchComments(enrichedRecommendations);
        }
    }, [enrichedRecommendations, loading, statsLoading]);

    // Debug useEffect to track profileUserData changes
    useEffect(() => {
        console.log('profileUserData changed:', {
            hasProfileUserData: !!profileUserData,
            profileUserDataKeys: profileUserData ? Object.keys(profileUserData) : [],
            userName: profileUserData?.userName,
            userUsername: profileUserData?.userUsername
        });
    }, [profileUserData]);

    // Fetch filter options when recommendations are loaded but filter data is missing
    useEffect(() => {
        const fetchMissingFilterOptions = async () => {
            if (enrichedRecommendations.length > 0 && availableServices.length === 0 && !isLoadingFilters && profileUserData?.userUsername && user?.id) {
                console.log('Fetching missing filter options...');
                try {
                    setIsLoadingFilters(true);
                    const params = new URLSearchParams();
                    params.set('loggedInUserId', user.id);
                    
                    if (selectedServices.length > 0) {
                        params.set('services', selectedServices.join(','));
                    }
                    if (selectedCities.length > 0) {
                        params.set('cities', selectedCities.join(','));
                    }
                    
                    const response = await fetch(
                        `${API_URL}/api/users/pro/${profileUserData.userUsername}/filter-options?${params.toString()}`
                    );
                    if (!response.ok) {
                        throw new Error('Failed to fetch filter options');
                    }
                    
                    const data = await response.json();
                    if (data.success) {
                        setAvailableServices(data.services || []);
                        setAvailableCities(data.cities || []);
                        setServiceIdCounts(data.serviceIdCounts || {});
                    }
                } catch (error) {
                    console.error('Error fetching missing filter options:', error);
                } finally {
                    setIsLoadingFilters(false);
                }
            }
        };
        
        fetchMissingFilterOptions();
    }, [enrichedRecommendations.length, availableServices.length, isLoadingFilters, profileUserData?.userUsername, user?.id, selectedServices, selectedCities]);

    const fetchListRecommendations = async (listId) => {
        try {
            const res = await fetch(
                `${API_URL}/api/lists/${listId}?user_id=${
                    user.id
                }&email=${encodeURIComponent(
                    user.primaryEmailAddress.emailAddress
                )}`
            );
            if (!res.ok)
                throw new Error("Failed to fetch list recommendations");
            const data = await res.json();
            return data.recommendations || [];
        } catch (err) {
            return [];
        }
    };

    const handleExpandList = async (listId) => {
        setExpandedListId(listId);
        if (!listRecs[listId]) {
            setListRecsLoading((prev) => ({ ...prev, [listId]: true }));
            const recs = await fetchListRecommendations(listId);
            setListRecs((prev) => ({ ...prev, [listId]: recs }));
            setListRecsLoading((prev) => ({ ...prev, [listId]: false }));
        }
    };

    const handleProviderLikeFromProfile = async (providerIdToLike) => {
        if (!user?.id || !user.primaryEmailAddress?.emailAddress) {
            alert("Please sign in to like/unlike this provider.");
            return;
        }
        if (!providerIdToLike) {
            console.error("Provider ID is missing for like action.");
            return;
        }

        const originalEnrichedRecommendations = JSON.parse(
            JSON.stringify(enrichedRecommendations)
        );
        const originalBaseRecommendations = JSON.parse(
            JSON.stringify(baseRecommendations)
        );
        const originalLikedRecommendationsSet = new Set(likedRecommendations);

        const isCurrentlyLiked = likedRecommendations.has(providerIdToLike);
        const newCurrentUserLikedState = !isCurrentlyLiked;

        const updateItems = (items) =>
            items.map((item) => {
                const itemProviderId = item.provider_id || item.id;
                if (itemProviderId === providerIdToLike) {
                    const currentItem =
                        originalEnrichedRecommendations.find(
                            (r) => (r.provider_id || r.id) === providerIdToLike
                        ) ||
                        originalBaseRecommendations.find(
                            (r) => (r.provider_id || r.id) === providerIdToLike
                        );
                    const currentItemNumLikes = currentItem
                        ? currentItem.num_likes || 0
                        : 0;

                    return {
                        ...item,
                        currentUserLiked: newCurrentUserLikedState,
                        num_likes: newCurrentUserLikedState
                            ? (parseInt(currentItemNumLikes, 10) || 0) + 1
                            : Math.max(
                                  0,
                                  (parseInt(currentItemNumLikes, 10) || 1) - 1
                              ),
                    };
                }
                return item;
            });

        setEnrichedRecommendations(updateItems(enrichedRecommendations));
        setBaseRecommendations(updateItems(baseRecommendations));

        if (newCurrentUserLikedState) {
            setLikedRecommendations((prev) =>
                new Set(prev).add(providerIdToLike)
            );
        } else {
            setLikedRecommendations((prev) => {
                const newSet = new Set(prev);
                newSet.delete(providerIdToLike);
                return newSet;
            });
        }

        try {
            const response = await fetch(
                `${API_URL}/api/providers/${providerIdToLike}/like`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        userId: user.id,
                        userEmail: user.primaryEmailAddress.emailAddress,
                    }),
                }
            );

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({
                    message: "Server error during like/unlike action.",
                }));
                throw new Error(
                    errorData.message ||
                        `Failed to update like status. Status: ${response.status}`
                );
            }
            const result = await response.json();

            const finalUpdateItems = (items) =>
                items.map((item) => {
                    const itemProviderId = item.provider_id || item.id;
                    if (itemProviderId === providerIdToLike) {
                        return {
                            ...item,
                            num_likes: parseInt(result.num_likes, 10) || 0,
                            currentUserLiked: result.currentUserLiked,
                        };
                    }
                    return item;
                });

            setEnrichedRecommendations((prevEnriched) =>
                finalUpdateItems(prevEnriched)
            );
            setBaseRecommendations((prevBase) => finalUpdateItems(prevBase));

            if (result.currentUserLiked) {
                setLikedRecommendations((prev) =>
                    new Set(prev).add(providerIdToLike)
                );
            } else {
                setLikedRecommendations((prev) => {
                    const newSet = new Set(prev);
                    newSet.delete(providerIdToLike);
                    return newSet;
                });
            }
        } catch (error) {
            console.error("Error liking provider from profile:", error.message);
            setEnrichedRecommendations(originalEnrichedRecommendations);
            setBaseRecommendations(originalBaseRecommendations);
            setLikedRecommendations(originalLikedRecommendationsSet);
            alert(`Failed to update like: ${error.message}`);
        }
    };

    const fetchUserTrustCirclesForModal = useCallback(async () => {
        if (!user?.primaryEmailAddress?.emailAddress) return [];
        setTrustCirclesLoading(true);
        setTrustCirclesError("");
        try {
            const email = encodeURIComponent(
                user.primaryEmailAddress.emailAddress
            );
            const res = await fetch(
                `${API_URL}/api/communities/user/${email}/communities`
            );
            if (!res.ok) {
                const errorData = await res.json().catch(() => ({}));
                throw new Error(
                    errorData.message || "Failed to fetch user trust circles."
                );
            }
            const data = await res.json();
            const formattedCircles = Array.isArray(data)
                ? data.map((tc) => ({ id: tc.id, name: tc.name }))
                : [];
            setUserTrustCircles(formattedCircles);
            return formattedCircles;
        } catch (err) {
            setTrustCirclesError(
                err.message || "Could not load trust circles."
            );
            setUserTrustCircles([]);
            return [];
        } finally {
            setTrustCirclesLoading(false);
        }
    }, [user]);

    const fetchUserReviewData = async (providerId) => {
        try {
            const response = await fetch(
                `${API_URL}/api/reviews/user-review?user_id=${user.id}&email=${encodeURIComponent(
                    user.primaryEmailAddress?.emailAddress
                )}&provider_id=${providerId}`
            );
            
            if (response.ok) {
                const reviewData = await response.json();
                return reviewData.review || null;
            }
        } catch (error) {
            console.error("Error fetching user review data:", error);
        }
        return null;
    };

    const handleOpenEditModal = async (rec) => {
        try {
            // Fetch the user's specific review data for this provider
            const providerId = rec.provider_id || rec.id;
            const reviewData = await fetchUserReviewData(providerId);
            
            // Enhance the recommendation object with review data
            const enhancedRec = {
                ...rec,
                // Use review data if available, otherwise fall back to existing fields
                rating: reviewData?.rating || rec.initial_rating || rec.rating || 0,
                visibility: reviewData?.visibility || rec.visibility || 'connections',
                recommender_message: reviewData?.content || rec.recommender_message || "",
                // Include community shares info for visibility logic
                trust_circle_ids: rec.trust_circle_ids || [],
                // Store the original review ID for updates
                review_id: reviewData?.id || null,
                // FIXED: Preserve the original image structure for backend processing
                // Check multiple sources for images and preserve the original format
                images: (() => {
                    // First try to get images from the original rec object
                    if (rec.images && Array.isArray(rec.images)) {
                        return rec.images;
                    }
                    // Fallback to visible_reviews if available
                    if (rec.visible_reviews && Array.isArray(rec.visible_reviews) && rec.visible_reviews.length > 0) {
                        return rec.visible_reviews[0].images || [];
                    }
                    // Last resort - empty array
                    return [];
                })()
            };
            
            setCurrentEditingRec(enhancedRec);
            setIsEditModalOpen(true);
            
            if (
                userTrustCircles.length === 0 &&
                !trustCirclesLoading &&
                !trustCirclesError
            )
                fetchUserTrustCirclesForModal();
        } catch (error) {
            console.error("Error preparing edit modal:", error);
            // Fallback to original behavior if there's an error
            setCurrentEditingRec(rec);
            setIsEditModalOpen(true);
        }
    };
    const handleUpdateRecommendationSuccess = async (responseData) => {
        const username = await fetchUsername();
        // fetchUsername now handles setting the profile data
        // Note: fetchUsername will call fetchAllProfileData with isInitialLoad=true
        // which will refresh connections and user score
    };

    // Review modal handlers
    const handleOpenReviewModal = (provider) => {
        // Create URL parameters for prefilling the recommendation form
        const params = new URLSearchParams();
        
        // Add the provider ID to link to existing service provider
        if (provider.id) {
            params.set('providerId', provider.id);
        }
        
        if (provider.business_name || provider.name) {
            params.set('businessName', provider.business_name || provider.name);
        }
        if (provider.contact_name) {
            params.set('contactName', provider.contact_name);
        }
        if (provider.website) {
            params.set('website', provider.website);
        }
        if (provider.phone_number) {
            params.set('phoneNumber', provider.phone_number);
        }
        if (provider.street_address) {
            params.set('streetAddress', provider.street_address);
        }
        if (provider.city) {
            params.set('city', provider.city);
        }
        if (provider.state) {
            params.set('state', provider.state);
        }
        if (provider.zip_code) {
            params.set('zipCode', provider.zip_code);
        }
        if (provider.google_place_id) {
            params.set('googlePlaceId', provider.google_place_id);
        }
        if (provider.latitude && provider.longitude) {
            params.set('latitude', provider.latitude);
            params.set('longitude', provider.longitude);
        }
        if (provider.tags && Array.isArray(provider.tags) && provider.tags.length > 0) {
            params.set('tags', provider.tags.join(', '));
        }
        
        // Navigate to share-recommendation page with prefilled data
        navigate(`/share-recommendation?${params.toString()}`);
    };

    const handleCloseReviewModal = () => {
        setIsReviewModalOpen(false);
        setProviderForReview(null);
    };

    const handleSubmitReview = async (reviewData) => {
        if (!providerForReview || !user) return;
        const session = window.Clerk.session;
        if (!session) {
            console.error("Clerk session not available");
            return;
        }
        const token = await session.getToken();
        if (!token) {
            console.error("Not authenticated, no token");
            return;
        }

        try {
            const response = await fetch(`${API_URL}/api/reviews`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    provider_id:
                        providerForReview.provider_id || providerForReview.id,
                    provider_email: providerForReview.email || "",
                    user_id: user.id,
                    email: user.primaryEmailAddress?.emailAddress,
                    rating: reviewData.rating,
                    content: reviewData.review,
                    tags: reviewData.tags,
                }),
            });
            if (!response.ok) throw new Error("Failed to submit review");

            handleCloseReviewModal();
            // Refresh recommendations to show updated stats
            const username = await fetchUsername();
            // fetchUsername now handles setting the profile data
            // Note: fetchUsername will call fetchAllProfileData with isInitialLoad=true
            // which will refresh connections and user score
        } catch (error) {
            console.error("Error submitting review:", error);
        }
    };

    // Batch fetch comments for multiple recommendations
    const fetchBatchComments = async (recommendations) => {
        if (!recommendations || recommendations.length === 0) return;

        setIsLoadingComments(true);
        try {
            const serviceIds = recommendations
                .map((rec) => rec.provider_id || rec.id)
                .filter(Boolean);

            if (serviceIds.length === 0) return;

            const response = await fetch(`${API_URL}/api/comments/batch`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ service_ids: serviceIds }),
            });

            if (response.ok) {
                const data = await response.json();
                if (data.success && data.comments) {
                    const commentsMap = new Map();
                    Object.entries(data.comments).forEach(
                        ([serviceId, comments]) => {
                            commentsMap.set(serviceId, comments || []);
                        }
                    );
                    setCommentsMap(commentsMap);
                }
            }
        } catch (error) {
            console.error("Error fetching batch comments:", error);
        } finally {
            setIsLoadingComments(false);
        }
    };

    // Handle new comment added
    const handleCommentAdded = (serviceId, newComment) => {
        setCommentsMap((prev) => {
            const newMap = new Map(prev);
            const existingComments = newMap.get(serviceId) || [];
            newMap.set(serviceId, [newComment, ...existingComments]);
            return newMap;
        });
    };

    const handleServiceSelection = (serviceId) => {
        setSelectedServices((prev) => {
            const newServices = prev.includes(serviceId) 
                ? prev.filter((s) => s !== serviceId) 
                : [...prev, serviceId];
            return newServices;
        });
        // Close the service filter dropdown when a selection is made
        setShowServiceFilter(false);
    };

    const handleCitySelection = (cityName) => {
        setSelectedCities((prev) => {
            const newCities = prev.includes(cityName) 
                ? prev.filter((c) => c !== cityName) 
                : [...prev, cityName];
            return newCities;
        });
        // Close the city filter dropdown when a selection is made
        setShowCityFilter(false);
    };

    const sortedRecommendations = React.useMemo(() => {
        let sortableItems = [...enrichedRecommendations];

        // Apply sorting only (filtering is now done server-side)
        if (
            sortOption === "topRated" &&
            sortableItems.every(
                (item) => typeof item.average_rating === "number"
            )
        )
            sortableItems.sort(
                (a, b) =>
                    (b.average_rating || 0) - (a.average_rating || 0) ||
                    (b.total_reviews || 0) - (a.total_reviews || 0)
            );
        else if (sortOption === "date_of_recommendation")
            sortableItems.sort(
                (a, b) =>
                    new Date(b.date_of_recommendation || b.created_at || 0) -
                    new Date(a.date_of_recommendation || a.created_at || 0)
            );
        return sortableItems;
    }, [
        enrichedRecommendations,
        sortOption,
    ]);

    const handleLogout = async () => {
        try {
            await signOut();
            navigate("/");
        } catch (err) {
            console.error("Error signing out:", err);
        }
    };
    const handleEditToggle = () => {
        if (isEditing) {
            setEditingBio(userBio);
            setImgSrcForCropper("");
            setCompletedCrop(null);
            setOriginalFile(null);
            setCrop(undefined);
        } else setEditingBio(userBio || "");
        setIsEditing(!isEditing);
    };
    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        const cropWidthInPercent = (MIN_DIMENSION / width) * 100;
        const cropVal = makeAspectCrop(
            { unit: "%", width: cropWidthInPercent },
            ASPECT_RATIO,
            width,
            height
        );
        const centeredCrop = centerCrop(cropVal, width, height);
        setCrop(centeredCrop);
    };
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setOriginalFile(file);
            setCrop(undefined);
            const reader = new FileReader();
            reader.addEventListener("load", () =>
                setImgSrcForCropper(reader.result?.toString() || "")
            );
            reader.readAsDataURL(file);
        }
    };

    const handleSaveProfile = async () => {
        if (!user) {
            setError("User not found.");
            return;
        }
        setError(null);
        setSaving(true);
        const formData = new FormData();
        formData.append("bio", editingBio);
        formData.append("firstName", user.firstName || "");
        formData.append("lastName", user.lastName || "");
        let fileToUpload = null;
        if (completedCrop && imgRef.current && originalFile) {
            try {
                const croppedImageBlob = await getCroppedImg(
                    imgRef.current,
                    completedCrop,
                    originalFile.name
                );
                fileToUpload = new File([croppedImageBlob], originalFile.name, {
                    type: croppedImageBlob.type,
                });
            } catch (cropError) {
                setError("Failed to crop image.");
                setSaving(false);
                return;
            }
        }
        if (fileToUpload) formData.append("profileImageFile", fileToUpload);
        try {
            const queryParams = getClerkUserQueryParams();
            if (!queryParams)
                throw new Error("User details not available for API query.");
            const response = await fetch(
                `${API_URL}/api/users/me/profile?${queryParams}`,
                { method: "PUT", body: formData }
            );
            const data = await response.json();
            if (!response.ok || !data.success)
                throw new Error(data.message || "Failed to update profile.");
            setUserBio(data.user.bio || "");
            setEditingBio(data.user.bio || "");
            // Note: Profile image upload functionality needs to be updated to handle GCS storage
            // For now, refresh the profile data to get the updated profile image URL
            const username = await fetchUsername();
            // fetchUsername now handles setting the profile data
            // Note: fetchUsername will call fetchAllProfileData with isInitialLoad=true
            // which will refresh connections and user score
            setImgSrcForCropper("");
            setCompletedCrop(null);
            setOriginalFile(null);
            setIsEditing(false);
            if (profileUserData)
                setProfileUserData((prev) => ({
                    ...prev,
                    userName: data.user.name,
                    userBio: data.user.bio,
                }));
        } catch (err) {
            setError(`Failed to save profile: ${err.message}`);
        } finally {
            setSaving(false);
        }
    };

    const onAvatarOrPreviewError = (e) => {
        e.target.style.display = "none";
        const fallbackParent =
            e.target.closest(".profile-avatar-display-wrapper") ||
            e.target.closest(".profile-avatar-cropper-wrapper");
        if (fallbackParent) {
            const fallbackIcon = fallbackParent.querySelector(
                ".profile-avatar-icon-fallback, .profile-avatar-icon-editing"
            );
            if (fallbackIcon) fallbackIcon.style.display = "flex";
        }
    };

    const displayOverallLoading =
        loading ||
        (statsLoading &&
            baseRecommendations.length > 0 &&
            enrichedRecommendations.length < baseRecommendations.length);

    const handleProviderClick = (providerId) => {
        // Open provider page in a new tab
        window.open(`/provider/${providerId}`, "_blank");
    };

    // Add this useEffect to get user location for map view
    useEffect(() => {
        if (viewMode === "map" && navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setUserLocation({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude,
                    });
                },
                (error) => {
                    //console.log("Geolocation error:", error);
                    // Set a default location if geolocation fails
                    setUserLocation({ lat: 39.8283, lng: -98.5795 });
                }
            );
        }
    }, [viewMode]);

    // Simplified loading condition - only show loading if we're actually loading and not signed out
    console.log('Render debug:', {
        isLoaded,
        loading,
        isSignedIn,
        hasInitialized,
        profileUserData: !!profileUserData,
        error,
        user: !!user
    });

    if (!isLoaded || (loading && isSignedIn)) {
        console.log('Showing loading screen');
        return (
            <div className="profile-loading-container">
                <div className="profile-spinner"></div>
                <p>Loading Profile...</p>
            </div>
        );
    }
    if (!isSignedIn && isLoaded) return null;
    if (!user && isLoaded && isSignedIn)
        return (
            <div className="profile-page">
                <div
                    className="profile-main-content"
                    style={{ textAlign: "center", paddingTop: "5rem" }}
                >
                    <h1 style={{ color: "var(--profile-primary-color)" }}>
                        Profile Access Denied
                    </h1>
                    <p
                        className="profile-error-banner"
                        style={{ margin: "1rem auto", maxWidth: "600px" }}
                    >
                        User information not available.
                    </p>
                    <button
                        className="profile-primary-action-btn"
                        onClick={() => navigate("/login")}
                    >
                        Go to Login
                    </button>
                </div>
            </div>
        );

    return (
        <div className="profile-page-container">
            {error && <div className="profile-error-banner">{error}</div>}
            <header className="profile-hero-header">
                <div className="profile-header-actions-top">
                    <button
                        className="profile-header-btn-top"
                        onClick={handleEditToggle}
                        disabled={saving}
                        title="Edit Profile"
                    >
                        <PencilSquareIcon className="btn-icon" />
                    </button>
                    <button
                        className="profile-header-btn-top"
                        onClick={handleGenerateInvite}
                        disabled={inviteLoading}
                        title="Invite Friends"
                    >
                        <UserPlusIcon className="btn-icon" />
                    </button>
                    <button
                        className="profile-header-btn-top"
                        onClick={() => setIsShareModalOpen(true)}
                        disabled={saving || isEditing}
                        title="Share Profile"
                    >
                        <ArrowUpTrayIcon className="btn-icon" />
                    </button>
                </div>
                <div className="profile-hero-content">
                    <div className="profile-avatar-wrapper">
                        {isEditing ? (
                            <div className="profile-avatar-cropper-wrapper">
                                <input
                                    type="file"
                                    id="profileImageUploadInput"
                                    style={{ display: "none" }}
                                    accept="image/*"
                                    onChange={handleFileChange}
                                />
                                {!imgSrcForCropper && (
                                    <div
                                        className="profile-avatar-container"
                                        onClick={() =>
                                            document
                                                .getElementById(
                                                    "profileImageUploadInput"
                                                )
                                                ?.click()
                                        }
                                        style={{ cursor: "pointer" }}
                                    >
                                        {profileImage ? (
                                            <img
                                                src={profileImage}
                                                alt="Current profile"
                                                className="profile-avatar-image"
                                                onError={onAvatarOrPreviewError}
                                            />
                                        ) : (
                                            <div className="profile-avatar-initials">
                                                <span>
                                                    {profileUserData?.userName?.[0]?.toUpperCase() ||
                                                        user?.firstName?.[0]?.toUpperCase() ||
                                                        user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() ||
                                                        "U"}
                                                </span>
                                            </div>
                                        )}
                                        <div className="profile-avatar-edit-overlay">
                                            <CameraIcon
                                                style={{
                                                    width: "24px",
                                                    height: "24px",
                                                }}
                                            />
                                        </div>
                                    </div>
                                )}
                                {imgSrcForCropper && (
                                    <div className="cropper-container">
                                        <ReactCrop
                                            crop={crop}
                                            onChange={(pc, p) => setCrop(p)}
                                            onComplete={(c) =>
                                                setCompletedCrop(c)
                                            }
                                            aspect={ASPECT_RATIO}
                                            minWidth={MIN_DIMENSION}
                                            minHeight={MIN_DIMENSION}
                                            circularCrop={true}
                                        >
                                            <img
                                                ref={imgRef}
                                                src={imgSrcForCropper}
                                                alt="Crop me"
                                                onLoad={onImageLoad}
                                                style={{ maxHeight: "300px" }}
                                            />
                                        </ReactCrop>
                                        <button
                                            className="profile-change-photo-btn-cropper"
                                            onClick={() =>
                                                document
                                                    .getElementById(
                                                        "profileImageUploadInput"
                                                    )
                                                    ?.click()
                                            }
                                        >
                                            <CameraIcon className="btn-icon" />{" "}
                                            Change Photo
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <>
                                {profileImage ? (
                                    <img
                                        src={profileImage}
                                        alt={
                                            profileUserData?.userName ||
                                            user?.firstName ||
                                            "User"
                                        }
                                        className="profile-avatar-image"
                                        onError={onAvatarOrPreviewError}
                                    />
                                ) : (
                                    <div className="profile-avatar-initials">
                                        <span>
                                            {profileUserData?.userName?.[0]?.toUpperCase() ||
                                                user?.firstName?.[0]?.toUpperCase() ||
                                                user?.primaryEmailAddress?.emailAddress?.[0]?.toUpperCase() ||
                                                "U"}
                                        </span>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    <div className="profile-details-wrapper">
                        <div className="profile-header-top">
                            <h1 className="profile-user-name">
                                {profileUserData?.userName ||
                                    `${user?.firstName || ""} ${
                                        user?.lastName || ""
                                    }`.trim() ||
                                    "User"}
                            </h1>
                            {isEditing && (
                                <div className="profile-header-actions">
                                    <button
                                        className="profile-save-btn"
                                        onClick={handleSaveProfile}
                                        disabled={saving}
                                    >
                                        <CheckCircleIcon className="btn-icon" />{" "}
                                        {saving
                                            ? "Saving..."
                                            : "Save Changes"}
                                    </button>
                                    <button
                                        className="profile-cancel-btn"
                                        onClick={handleEditToggle}
                                        disabled={saving}
                                    >
                                        <XCircleIcon className="btn-icon" />{" "}
                                        Cancel
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className="profile-contact-info">
                            {profileUserData?.userPhone && (
                                <a
                                    href={`sms:${profileUserData.userPhone.replace(
                                        /\D/g,
                                        ""
                                    )}`}
                                    className="profile-contact-link phone"
                                    title="Send a text to this number"
                                >
                                    <svg
                                        className="contact-icon"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2zm-2 12H6v-2h12v2zm0-3H6V9h12v2zm0-3H6V6h12v2z" />
                                    </svg>
                                    {profileUserData.userPhone}
                                </a>
                            )}

                            {profileUserData?.userEmail && (
                                <a
                                    href={`mailto:${profileUserData.userEmail}`}
                                    className="profile-contact-link email"
                                    title="Send email to this address"
                                >
                                    <EnvelopeIcon className="contact-icon" />
                                    {profileUserData.userEmail}
                                </a>
                            )}
                        </div>

                        {isEditing ? (
                            <textarea
                                className="profile-bio-textarea"
                                value={editingBio}
                                onChange={(e) => setEditingBio(e.target.value)}
                                placeholder="Tell us about yourself..."
                                rows={3}
                            />
                        ) : (
                            userBio && (
                                <blockquote className="profile-user-bio">
                                    <p>{userBio}</p>
                                </blockquote>
                            )
                        )}
                    </div>
                    <div className="profile-stats-wrapper">
                        <div className="profile-stat-item">
                            <span className="profile-stat-number">
                                {totalRecommendationCount}
                            </span>
                            <span className="profile-stat-label">
                                Recommendations
                            </span>
                        </div>
                        <div className="profile-stat-item">
                            <span className="profile-stat-number">
                                {connections.length}
                            </span>
                            <span className="profile-stat-label">
                                Followers
                            </span>
                        </div>
                        <div className="profile-stat-item">
                            <div className="profile-trust-score-wrapper">
                                <TrustScoreWheel
                                    score={userScore}
                                    showDebug={false}
                                />
                            </div>
                            {/* <span className="profile-stat-label">
                                Trust Points
                            </span> */}
                        </div>
                        <div
                            className="profile-stat-item profile-achievement-hidden"
                            style={{ display: "none" }}
                        >
                            <div className="profile-stat-achievement">
                                <AchievementBadge
                                    recCount={totalRecommendationCount}
                                    onStartRecommending={() =>
                                        navigate("/share-recommendation")
                                    }
                                />
                            </div>
                            <span className="profile-stat-label">
                                {(() => {
                                    const count =
                                        totalRecommendationCount;
                                    if (count >= 100)
                                        return "Diamond Recommender";
                                    if (count >= 50)
                                        return "Platinum Recommender";
                                    if (count >= 25) return "Gold Recommender";
                                    if (count >= 10)
                                        return "Silver Recommender";
                                    if (count >= 1) return "Bronze Recommender";
                                    return "Start Your Journey";
                                })()}
                            </span>
                            {(() => {
                                const count = totalRecommendationCount;
                                let nextTier, remaining;
                                if (count < 1) {
                                    nextTier = "Bronze";
                                    remaining = 1 - count;
                                } else if (count < 10) {
                                    nextTier = "Silver";
                                    remaining = 10 - count;
                                } else if (count < 25) {
                                    nextTier = "Gold";
                                    remaining = 25 - count;
                                } else if (count < 50) {
                                    nextTier = "Platinum";
                                    remaining = 50 - count;
                                } else if (count < 100) {
                                    nextTier = "Diamond";
                                    remaining = 100 - count;
                                } else {
                                    return null; // Max tier reached
                                }
                                return (
                                    <span className="profile-stat-progress">
                                        {count === 0
                                            ? "Share your first recommendation!"
                                            : `${remaining} more to reach ${nextTier}`}
                                    </span>
                                );
                            })()}
                        </div>
                    </div>
                </div>
            </header>
            <main className="profile-content-area">
                <section style={{ marginBottom: "2rem" }}>
                    <div className="profile-recommendations-header">
                        <div className="profile-recommendations-title-section">
                            <h2>My Lists</h2>
                            <div className="profile-recommendations-controls">
                                <button
                                    className="profile-add-new-btn"
                                    onClick={() =>
                                        navigate("/share-recommendation")
                                    }
                                >
                                    <PlusCircleIcon className="btn-icon" /> Add
                                    New
                                </button>
                            </div>
                        </div>
                    </div>
                    {listsLoading ? (
                        <div>Loading lists...</div>
                    ) : listsError ? (
                        <div className="profile-error-banner">{listsError}</div>
                    ) : userLists.length === 0 ? (
                        <div>You haven't created any lists yet.</div>
                    ) : (
                        <div className="profile-list-card-row">
                            {userLists.map((list) => (
                                <ListCard
                                    key={list.id}
                                    list={list}
                                    expanded={expandedListId === list.id}
                                    onExpand={handleExpandList}
                                    recommendations={listRecs[list.id] || []}
                                    loading={!!listRecsLoading[list.id]}
                                />
                            ))}
                        </div>
                    )}
                </section>

                <div className="profile-recommendations-header">
                    <div className="profile-recommendations-title-section">
                        <h2>My Recommendations</h2>
                        <div className="profile-recommendations-controls">
                            <button
                                className="profile-add-new-btn"
                                onClick={() =>
                                    navigate("/share-recommendation")
                                }
                            >
                                <PlusCircleIcon className="btn-icon" /> Add New
                            </button>
                        </div>
                    </div>
                    {(enrichedRecommendations.length > 0 || availableServices.length > 0 || availableCities.length > 1) && (
                        <div className="profile-search-wrapper">
                            <div className="filters-container">
                                {(availableServices.length > 0 || isLoadingFilters) && (
                                    <div className="profile-city-filter-toggle-section">
                                        <button
                                            className="profile-city-filter-toggle"
                                            onClick={() =>
                                                setShowServiceFilter(
                                                    !showServiceFilter
                                                )
                                            }
                                            disabled={isLoadingFilters}
                                        >
                                            <FaConciergeBell className="profile-filter-icon" />
                                            <span className="filter-button-text">
                                                <span className="filter-button-text-long">
                                                    Filter by{" "}
                                                </span>
                                                <span>{isLoadingFilters ? "Loading..." : "Service"}</span>
                                            </span>
                                            {selectedServices.length > 0 && (
                                                <span className="profile-active-filters-badge">
                                                    {selectedServices.length}
                                                </span>
                                            )}
                                            <ChevronDownIcon
                                                className={`profile-filter-chevron ${
                                                    showServiceFilter
                                                        ? "rotated"
                                                        : ""
                                                }`}
                                            />
                                        </button>
                                    </div>
                                )}
                                {(availableCities.length > 1 || isLoadingFilters) && (
                                    <div className="profile-city-filter-toggle-section">
                                        <button
                                            className="profile-city-filter-toggle"
                                            onClick={() =>
                                                setShowCityFilter(
                                                    !showCityFilter
                                                )
                                            }
                                            disabled={isLoadingFilters}
                                        >
                                            <FaMapMarkerAlt className="profile-filter-icon" />
                                            <span className="filter-button-text">
                                                <span className="filter-button-text-long">
                                                    Filter by{" "}
                                                </span>
                                                <span>{isLoadingFilters ? "Loading..." : "City"}</span>
                                            </span>
                                            {selectedCities.length > 0 && (
                                                <span className="profile-active-filters-badge">
                                                    {selectedCities.length}
                                                </span>
                                            )}
                                            <ChevronDownIcon
                                                className={`profile-filter-chevron ${
                                                    showCityFilter
                                                        ? "rotated"
                                                        : ""
                                                }`}
                                            />
                                        </button>
                                    </div>
                                )}
                                {showServiceFilter &&
                                    availableServices.length > 0 && !isLoadingFilters && (
                                        <div className="profile-city-filter-wrapper">
                                            <div className="profile-city-filter-checkboxes">
                                                {availableServices.map(
                                                    (service) => (
                                                        <div
                                                            key={service.id}
                                                            className="profile-city-checkbox-item"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                id={`service-${service.id}`}
                                                                name={service.id}
                                                                checked={selectedServices.includes(
                                                                    service.id
                                                                )}
                                                                onChange={() =>
                                                                    handleServiceSelection(
                                                                        service.id
                                                                    )
                                                                }
                                                            />
                                                            <label
                                                                htmlFor={`service-${service.id}`}
                                                                className="profile-city-checkbox-label"
                                                            >
                                                                {service.name}
                                                            </label>
                                                            <span className="profile-city-count">
                                                                ({service.count})
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                                {selectedServices.length >
                                                    0 && (
                                                    <button
                                                        onClick={() =>
                                                            setSelectedServices(
                                                                []
                                                            )
                                                        }
                                                        className="profile-city-clear-all"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                {showCityFilter &&
                                    availableCities.length > 1 && !isLoadingFilters && (
                                        <div className="profile-city-filter-wrapper">
                                            <div className="profile-city-filter-checkboxes">
                                                {availableCities.map(
                                                    (city) => (
                                                        <div
                                                            key={city.name}
                                                            className="profile-city-checkbox-item"
                                                        >
                                                            <input
                                                                type="checkbox"
                                                                id={`city-${city.name.replace(
                                                                    /\s+/g,
                                                                    "-"
                                                                )}`}
                                                                name={city.name}
                                                                checked={selectedCities.includes(
                                                                    city.name
                                                                )}
                                                                onChange={() =>
                                                                    handleCitySelection(
                                                                        city.name
                                                                    )
                                                                }
                                                            />
                                                            <label
                                                                htmlFor={`city-${city.name.replace(
                                                                    /\s+/g,
                                                                    "-"
                                                                )}`}
                                                                className="profile-city-checkbox-label"
                                                            >
                                                                {city.name}
                                                            </label>
                                                            <span className="profile-city-count">
                                                                ({city.count})
                                                            </span>
                                                        </div>
                                                    )
                                                )}
                                                {selectedCities.length > 0 && (
                                                    <button
                                                        onClick={() =>
                                                            setSelectedCities(
                                                                []
                                                            )
                                                        }
                                                        className="profile-city-clear-all"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                {isLoadingFilters && (
                                    <div className="profile-filter-loading">
                                        <div className="profile-spinner small"></div>
                                        <span>Loading filters...</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
                {(loading ||
                    (statsLoading &&
                        baseRecommendations.length > 0 &&
                        enrichedRecommendations.length <
                            baseRecommendations.length)) &&
                    sortedRecommendations.length === 0 && (
                        <div className="profile-loading-container small-spinner">
                            <div className="profile-spinner"></div>
                            <p>Loading recommendations...</p>
                        </div>
                    )}
                {!(
                    loading ||
                    (statsLoading &&
                        baseRecommendations.length > 0 &&
                        enrichedRecommendations.length <
                            baseRecommendations.length)
                ) &&
                    sortedRecommendations.length > 0 && (
                        <>
                            {/* View Mode Tabs */}
                            <div className="view-mode-tabs">
                                <button
                                    className={`view-mode-tab ${
                                        viewMode === "list" ? "active" : ""
                                    }`}
                                    onClick={() => setViewMode("list")}
                                >
                                    <FaList className="tab-icon" />
                                    <span>List View</span>
                                    <span className="result-count">
                                        ({sortedRecommendations.length})
                                    </span>
                                </button>
                                <button
                                    className={`view-mode-tab ${
                                        viewMode === "map" ? "active" : ""
                                    }`}
                                    onClick={() => setViewMode("map")}
                                >
                                    <FaMap className="tab-icon" />
                                    <span>Map View</span>
                                    <span className="result-count">
                                        (
                                        {
                                            sortedRecommendations.filter(
                                                (rec) => rec.latitude && rec.longitude
                                            ).length
                                        }
                                        )
                                    </span>
                                </button>
                            </div>

                            {/* List View */}
                            {viewMode === "list" && (
                                <ul className="provider-list">
                                    {sortedRecommendations.map((rec) => (
                                        <ProfileRecommendationCard
                                            key={rec.id}
                                            rec={rec}
                                            onEdit={handleOpenEditModal}
                                            onLikeRecommendation={
                                                handleProviderLikeFromProfile
                                            }
                                            onRefreshList={async () => {
                                                const username = await fetchUsername();
                                                // fetchUsername now handles setting the profile data
                                                // Note: fetchUsername will call fetchAllProfileData with isInitialLoad=true
                                                // which will refresh connections and user score
                                            }}
                                            user={user}
                                            comments={
                                                commentsMap.get(
                                                    rec.provider_id || rec.id
                                                ) || []
                                            }
                                            onCommentAdded={handleCommentAdded}
                                            currentUserName={currentUserName}
                                            onWriteReview={handleOpenReviewModal}
                                        />
                                    ))}
                                </ul>
                            )}

                            {/* Map View */}
                            {viewMode === "map" && (
                                <div className="map-view-container">
                                    <MapView
                                        providers={sortedRecommendations.filter(
                                            (rec) => rec.latitude && rec.longitude
                                        )}
                                        userLocation={userLocation}
                                        onProviderClick={handleProviderClick}
                                    />
                                </div>
                            )}
                        </>
                    )}
                {!(
                    loading ||
                    (statsLoading &&
                        baseRecommendations.length > 0 &&
                        enrichedRecommendations.length <
                            baseRecommendations.length)
                ) &&
                    sortedRecommendations.length === 0 &&
                    !error &&
                    (selectedCities.length > 0 ||
                    selectedServices.length > 0 ? (
                        <div className="profile-empty-state no-search-results">
                            <svg
                                className="no-search-icon"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            <p>
                                No recommendations found
                                {selectedServices.length > 0 &&
                                    ` for ${selectedServices.join(", ")}`}
                                {selectedCities.length > 0 &&
                                    ` in ${selectedCities.join(", ")}`}
                            </p>
                            <p className="search-suggestions">
                                Try adjusting your filters.
                            </p>
                            <div className="profile-filter-clear-buttons">
                                {selectedServices.length > 0 && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => setSelectedServices([])}
                                    >
                                        Clear Service Filters
                                    </button>
                                )}
                                {selectedCities.length > 0 && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => setSelectedCities([])}
                                    >
                                        Clear City Filters
                                    </button>
                                )}
                                {(selectedCities.length > 0 ||
                                    selectedServices.length > 0) && (
                                    <button
                                        className="profile-secondary-action-btn"
                                        onClick={() => {
                                            setSelectedCities([]);
                                            setSelectedServices([]);
                                        }}
                                    >
                                        Clear All Filters
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="profile-empty-state no-providers-message">
                            <FaStar className="no-providers-icon" />
                            <p>You haven't made any recommendations yet.</p>
                            <button
                                className="profile-primary-action-btn"
                                onClick={() =>
                                    navigate("/share-recommendation")
                                }
                            >
                                Share Your First Recommendation
                            </button>
                        </div>
                    ))}
                {!(
                    loading ||
                    (statsLoading &&
                        baseRecommendations.length > 0 &&
                        enrichedRecommendations.length <
                            baseRecommendations.length)
                ) &&
                    sortedRecommendations.length === 0 &&
                    error && (
                        <p className="profile-empty-state-error-inline">
                            Could not load recommendations. {error}
                        </p>
                    )}
            </main>
            {isEditModalOpen && currentEditingRec && user && (
                <EditRecommendationModal
                    isOpen={isEditModalOpen}
                    onClose={() => {
                        setIsEditModalOpen(false);
                        setCurrentEditingRec(null);
                    }}
                    recommendationToEdit={currentEditingRec}
                    onSaveSuccess={handleUpdateRecommendationSuccess}
                    userEmail={user.primaryEmailAddress?.emailAddress}
                    clerkUserId={user.id}
                    apiBaseUrl={API_URL}
                    userTrustCirclesProp={userTrustCircles}
                    trustCirclesLoadingProp={trustCirclesLoading}
                    trustCirclesErrorProp={trustCirclesError}
                    fetchUserTrustCirclesFunc={fetchUserTrustCirclesForModal}
                />
            )}
            {isShareModalOpen && (
                <ShareProfileModal
                    isOpen={isShareModalOpen}
                    onClose={() => setIsShareModalOpen(false)}
                    profileData={profileUserData}
                    userEmail={user?.primaryEmailAddress?.emailAddress}
                />
            )}
            {isReviewModalOpen && providerForReview && (
                <ReviewModal
                    isOpen={isReviewModalOpen}
                    onClose={handleCloseReviewModal}
                    onSubmit={handleSubmitReview}
                    provider={providerForReview}
                />
            )}
            
            {/* Invite Modal */}
            {isInviteModalOpen && (
                <div className="profile-edit-modal-overlay">
                    <div className="profile-edit-modal-content">
                        <button
                            onClick={() => {
                                setIsInviteModalOpen(false);
                                setInviteSuccess(null);
                                setInviteError(null);
                            }}
                            className="profile-edit-modal-close-btn"
                        >
                            &times;
                        </button>
                        <h2 className="profile-edit-modal-title">
                            Invite Someone to Tried & Trusted
                        </h2>
                        <div className="profile-edit-modal-body">
                            {inviteError && (
                                <div className="profile-edit-modal-message error">
                                    <XCircleIcon />
                                    <span>{inviteError}</span>
                                </div>
                            )}
                            
                            {inviteSuccess && (
                                <div className="profile-edit-modal-message success">
                                    <CheckCircleIcon />
                                    <span className="invite-success-message">
                                        Invite link generated successfully!
                                    </span>
                                </div>
                            )}
                            
                            <div className="invite-link-section">
                                <p>Share this invite link with someone you trust:</p>
                                {inviteSuccess && (
                                    <div className="invite-link-container">
                                        <input
                                            type="text"
                                            value={inviteSuccess}
                                            readOnly
                                            className="invite-link-input"
                                        />
                                        <button
                                            onClick={handleCopyInviteLink}
                                            className="profile-edit-modal-btn primary-btn"
                                        >
                                            Copy Link
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            <div className="profile-edit-modal-button-row">
                                <button
                                    className="profile-edit-modal-btn cancel-btn"
                                    onClick={() => {
                                        setIsInviteModalOpen(false);
                                        setInviteSuccess(null);
                                        setInviteError(null);
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Profile;
